// Overview (§11 `/`): due-soon, 24h failures, success-rate, scheduling-lag/backlog/flow-control/
// heartbeat tiles. Polls the derived /overview snapshot. The heartbeat panel is the dead-man's-
// switch surface (§9.6) — a stale tick is the single most important stability signal.

import { Api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtLagMs, fmtPercent, fmtRelative } from "../lib/format";
import { Card, ErrorBox, Spinner, Tile, Badge } from "../components/ui";

export function OverviewPage() {
  const { data, error, loading, reload } = useAsync(() => Api.overview(), []);

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const sr = data.last24h.successRate;
  const srTone = sr == null ? undefined : sr >= 0.99 ? "ok" : sr >= 0.95 ? "warn" : "danger";
  const lagTone = data.schedulingLagMs > 120_000 ? "danger" : data.schedulingLagMs > 30_000 ? "warn" : "ok";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Overview</h1>
        <button className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Tile label="Due (5 min)" value={data.dueSoon} />
        <Tile label="Failures 24h" value={data.last24h.failed} tone={data.last24h.failed > 0 ? "warn" : undefined} />
        <Tile label="Success rate 24h" value={fmtPercent(sr)} tone={srTone} />
        <Tile label="DLQ backlog" value={data.dlqBacklog} tone={data.dlqBacklog > 0 ? "danger" : "ok"} />
        <Tile label="In-flight" value={data.inFlight} />
        <Tile label="Scheduling lag" value={fmtLagMs(data.schedulingLagMs)} tone={lagTone} />
      </div>

      <Card title="24h run outcomes">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Stat label="Succeeded" value={data.last24h.succeeded} />
          <Stat label="Failed" value={data.last24h.failed} />
          <Stat label="Dead" value={data.last24h.dead} />
          <Stat label="Skipped" value={data.last24h.skipped} />
          <Stat label="Total" value={data.last24h.total} />
        </div>
      </Card>

      <Card title="Heartbeats (dead-man's-switch)">
        {data.heartbeats.length === 0 ? (
          <div className="text-sm text-muted">No heartbeats recorded yet.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">Component</th>
                <th className="th">Last beat</th>
                <th className="th">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.heartbeats.map((h) => (
                <tr key={h.component}>
                  <td className="td mono">{h.component}</td>
                  <td className="td mono">{fmtRelative(h.lastBeatAt)}</td>
                  <td className="td">
                    {h.stale ? <Badge tone="danger">STALE</Badge> : <Badge tone="ok">LIVE</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!data.tickAlive && (
          <div className="mt-3 rounded border border-danger/50 bg-danger/10 p-2 text-sm text-danger">
            Tick heartbeat is stale or missing — the scheduler may have stopped firing.
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="label">{label}</div>
      <div className="font-mono text-lg text-slate-100">{value}</div>
    </div>
  );
}